/* --------------------------------------------------------------------------
   Vanguarda carousel enhancement.

   The theme already renders horizontal scrollers (.slider, .proof__track, and
   the product gallery on mobile). Three things were missing:

     1. On desktop .slider collapsed into a static grid with overflow:visible,
        so there was nothing to scroll at all.  Fixed in CSS.
     2. Native horizontal scroll only responds to touch and trackpads. With a
        mouse there is no way to move the track, and no visible affordance that
        it moves.
     3. No arrows.

   This file adds pointer-drag and injected arrow buttons to any track that
   actually overflows, and stays out of the way when it doesn't. No Liquid
   changes: it finds the tracks by class, so it applies to every section that
   already uses them, including ones added later in the editor.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var SELECTOR = '.slider, .proof__track, .product__gallery';
  var DRAG_THRESHOLD = 6; // px of movement before we treat it as a drag, not a click

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function stepWidth(track) {
    var first = track.firstElementChild;
    if (!first) return track.clientWidth * 0.8;
    var box = first.getBoundingClientRect().width;
    // The gap counts: the next card starts a full gap further along, and
    // scrolling by the card width alone leaves the track a gap short of the
    // snap point every time.
    var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return box + gap;
  }

  function pageWidth(track) {
    var step = stepWidth(track);
    var perView = Math.max(1, Math.round(track.clientWidth / step));
    // Whole cards, deliberately.
    //
    // This used to subtract 0.15 of a card to leave a sliver showing. With
    // scroll-snap-type: x mandatory the browser then snapped to the nearest
    // card start anyway — and 85% of the way there rounds backwards, so a
    // press advanced one card fewer than it should and the track appeared to
    // rubber-band. The sliver the old maths wanted is already there: it is the
    // peek built into grid-auto-columns.
    return step * perView;
  }

  /* The arrows are centred on the slide's IMAGE, not on the whole slide. A
     product card is image plus index, title, descriptor, swatches and price, so
     the midpoint of the card sits well below the midpoint of the photo. CSS
     cannot know that height — the card ratio is a percentage of a column width
     that changes with the viewport — so it is measured here and handed over as
     a custom property. */
  function measureNav(track, wrap) {
    var first = track.firstElementChild;
    if (!first) return;
    var media = first.querySelector('.card__media, .tile, img') || first;
    var h = media.getBoundingClientRect().height;
    if (h > 24) wrap.style.setProperty('--vg-nav-top', Math.round(h / 2) + 'px');
  }

  function overflows(track) {
    return track.scrollWidth - track.clientWidth > 4;
  }

  function build(track) {
    if (track.dataset.vgCarousel) return;
    track.dataset.vgCarousel = 'true';

    var wrap = document.createElement('div');
    wrap.className = 'vg-carousel';
    track.parentNode.insertBefore(wrap, track);
    wrap.appendChild(track);

    var prev = document.createElement('button');
    var next = document.createElement('button');
    [prev, next].forEach(function (b, i) {
      b.type = 'button';
      b.className = 'vg-carousel__nav vg-carousel__nav--' + (i ? 'next' : 'prev');
      b.setAttribute('aria-label', i ? 'Next' : 'Previous');
      b.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M' + (i ? '9 5l7 7-7 7' : '15 5l-7 7 7 7') + '"/></svg>';
      wrap.appendChild(b);
    });

    function go(dir) {
      track.scrollBy({ left: dir * pageWidth(track), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });

    function sync() {
      var on = overflows(track);
      wrap.classList.toggle('is-scrollable', on);
      measureNav(track, wrap);
      if (!on) return;
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    /* A scrollable region needs to be reachable without a mouse. The keyboard
       handler below was already here but unreachable: with no tabindex the
       track could never take focus, so the arrow keys never arrived. */
    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');
    if (!track.hasAttribute('role')) track.setAttribute('role', 'region');
    if (!track.hasAttribute('aria-label')) {
      track.setAttribute('aria-label', 'Scrollable products');
    }

    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    // images landing late change scrollWidth
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', sync, { once: true });
    });
    sync();

    /* ---- pointer drag ------------------------------------------------- */
    var down = false, moved = false, startX = 0, startScroll = 0, pid = null;

    track.addEventListener('pointerdown', function (e) {
      // Let touch keep native momentum scrolling; only take over for mouse/pen.
      if (e.pointerType === 'touch' || e.button !== 0) return;
      if (!overflows(track)) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      pid = e.pointerId;
      // Not is-grabbing yet. That class switches pointer-events off on every
      // link in the track, and adding it on pointerdown meant a plain click
      // released onto nothing clickable: the card never opened. It goes on
      // only below, once the pointer has travelled far enough to be a drag.
    });

    track.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!moved) {
        moved = true;
        track.classList.add('is-grabbing');
        // Capture only once we know it is a drag, so a plain click still
        // reaches the card's link.
        try { track.setPointerCapture(pid); } catch (err) {}
        // scroll-snap fights a manual drag; suspend it until release.
        track.style.scrollSnapType = 'none';
      }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    });

    function release(e) {
      if (!down || (e && e.pointerId !== pid)) return;
      down = false;
      track.classList.remove('is-grabbing');
      try { track.releasePointerCapture(pid); } catch (err) {}
      track.style.scrollSnapType = '';
      if (moved) {
        // Swallow the click that follows the drag, so releasing over a card
        // does not navigate to it.
        var swallow = function (ev) {
          ev.stopPropagation();
          ev.preventDefault();
          track.removeEventListener('click', swallow, true);
        };
        track.addEventListener('click', swallow, true);
        // If the drag ended outside the track no click follows, and a
        // swallower left armed would eat the shopper's next real click.
        setTimeout(function () {
          moved = false;
          track.removeEventListener('click', swallow, true);
        }, 0);
      }
    }
    // Links and images are natively draggable: pressing on a photo and moving
    // starts the browser's ghost-image drag, which cancels the pointer stream
    // and the carousel never moves. The track's own drag replaces it.
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    track.addEventListener('pointerup', release);
    track.addEventListener('pointercancel', release);
    track.addEventListener('lostpointercapture', release);

    /* ---- keyboard ----------------------------------------------------- */
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { go(1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { go(-1); e.preventDefault(); }
    });
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(build);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
